import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Stack,
  TextField,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Chip,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import BuildIcon from '@mui/icons-material/Build';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EmailIcon from '@mui/icons-material/Email';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { useAuth } from '../../components/auth/AuthContext';
import AdminTabs from '../../components/admin/AdminTabs';
import SplitOrder from '../../components/admin/SplitOrder';
import { Order, OrderResponse } from '../../types/order';
import { APIHOST } from '../../common';
import { fetchOrder } from '@/src/lib/order';

export default function OrdersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState<Order[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string>('');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  // Sorting state
  const [orderBy, setOrderBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  
  // Toggle state for showing all orders vs only received orders
  const [showReceivedDraftOrders, setShowReceivedDraftOrders] = useState<'all' | 'received' | 'abandoned'>('all');
  
  // Toggle state for invoice filter
  const [invoiceFilter, setInvoiceFilter] = useState<'all' | 'invoiced' | 'not-invoiced'>('all');
  
  // Toggle state for credit card filter
  const [creditCardFilter, setCreditCardFilter] = useState<'all' | 'added' | 'not-added'>('all');
  
  // Conference and Conference Group filters
  const [conferences, setConferences] = useState<any[]>([]);
  const [conferenceGroups, setConferenceGroups] = useState<any[]>([]);
  const [conferenceHotels, setConferenceHotels] = useState<any[]>([]);
  const [selectedConference, setSelectedConference] = useState<string>('all');
  const [selectedConferenceGroup, setSelectedConferenceGroup] = useState<string>('all');
  const [selectedConferenceHotel, setSelectedConferenceHotel] = useState<string>('all');

  // Selection state for bulk actions
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);

  // Split order dialog state
  const [splitOrderDialogOpen, setSplitOrderDialogOpen] = useState(false);
  const [selectedOrderForSplit, setSelectedOrderForSplit] = useState<string>('');

  const isAuthenticated = currentUser && (currentUser.userType === 'internal' || currentUser.userType === 'admin');

  useEffect(() => {
    // Check authentication
    if (isAuthenticated) {
      fetchConferencesAndGroups();
      fetchOrders();
    } 
    setLoading(false);
  }, [navigate, isAuthenticated, page, rowsPerPage, orderBy, order, showReceivedDraftOrders, invoiceFilter, creditCardFilter, selectedConference, selectedConferenceGroup, selectedConferenceHotel, searchParams]);

  // Clear selection when orders change
  useEffect(() => {
    setSelectedOrders([]);
  }, [orders]);

  const fetchConferencesAndGroups = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      // Fetch conferences
      const conferencesResponse = await fetch(
        `${APIHOST}/api/conferences?sort=name:asc`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (conferencesResponse.ok) {
        const conferencesData = await conferencesResponse.json();
        setConferences(conferencesData.data || []);
      }

      // Fetch conference groups
      const groupsResponse = await fetch(
        `${APIHOST}/api/conference-groups?sort=name:asc&populate=conference&pagination[pageSize]=200&pagination[page]=1`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (groupsResponse.ok) {
        const groupsData = await groupsResponse.json();
        setConferenceGroups(groupsData.data || []);
      }

      // Fetch conference hotels
      const hotelsResponse = await fetch(
        `${APIHOST}/api/conference-hotels?sort=hotel.longName:asc&populate=hotel&pagination[pageSize]=200&pagination[page]=1`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (hotelsResponse.ok) {
        const hotelsData = await hotelsResponse.json();
        setConferenceHotels(hotelsData.data || []);
      }
    } catch (err) {
      console.error('Error fetching conferences, groups, and hotels:', err);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Build search filters
      let searchFilters = '';
      if (searchQuery.trim()) {
        const query = encodeURIComponent(searchQuery.trim());
        searchFilters = `&filters[$or][0][confirmation][$containsi]=${query}&filters[$or][1][contactEmail][$containsi]=${query}&filters[$or][2][contactFirstName][$containsi]=${query}&filters[$or][3][contactLastName][$containsi]=${query}&filters[$or][4][billingAddressee][$containsi]=${query}`;
      }
      
      // Add order status filter based on toggle
      let statusFilter = '';
      if (showReceivedDraftOrders === 'received') {
        statusFilter = '&filters[orderStatus][$eq]=received';
      } else if (showReceivedDraftOrders === 'abandoned') {
        statusFilter = '&filters[orderStatus][$eq]=abandoned';
      }

      // Add invoice status filter
      let invoiceStatusFilter = '';
      if (invoiceFilter === 'invoiced') {
        invoiceStatusFilter = '&filters[invoiceSent][$notNull]=true';
      } else if (invoiceFilter === 'not-invoiced') {
        invoiceStatusFilter = '&filters[invoiceSent][$null]=true';
      }

      // Add credit card status filter
      let creditCardStatusFilter = '';
      if (creditCardFilter === 'added') {
        creditCardStatusFilter = '&filters[creditCardAdded][$eq]=true';
      } else if (creditCardFilter === 'not-added') {
        creditCardStatusFilter = '&filters[creditCardAdded][$ne]=true';
      }

      // Add conference filter
      let conferenceFilter = '';
      if (selectedConference !== 'all') {
        conferenceFilter = `&filters[conference_group][conference][documentId][$eq]=${selectedConference}`;
      }

      // Add conference group filter
      let conferenceGroupFilter = '';
      if (selectedConferenceGroup !== 'all') {
        conferenceGroupFilter = `&filters[conference_group][documentId][$eq]=${selectedConferenceGroup}`;
      }

      // Add conference hotel filter
      let conferenceHotelFilter = '';
      if (selectedConferenceHotel !== 'all') {
        conferenceHotelFilter = `&filters[conference_hotel][documentId][$eq]=${selectedConferenceHotel}`;
      }

      // Add URL filters if present
      let urlFilters = '';
      const urlParams = new URLSearchParams(window.location.search);
      for (const [key, value] of urlParams.entries()) {
        if (key.startsWith('filters[') && key.endsWith(']') && value) {
          // Extract the filter field from the key (e.g., 'filters[conference_hotel_room]' -> 'conference_hotel_room')
          const filterField = key.slice(8, -1); // Remove 'filters[' and ']'
          urlFilters += `&filters[order_rooms][conference_hotel_room][documentId][$eq]=${encodeURIComponent(value)}`;
        }
      }

      // Build pagination and sorting parameters
      const paginationParams = `&pagination[page]=${page + 1}&pagination[pageSize]=${rowsPerPage}`;
      const sortParams = `&sort=${orderBy}:${order}`;

      const response = await fetch(

        `${APIHOST}/api/orders?populate[0]=conference_group.conference&populate[1]=conference_hotel.hotel&populate[2]=order_rooms.conference_hotel_room.conference_hotel.hotel${searchFilters}${statusFilter}${invoiceStatusFilter}${creditCardStatusFilter}${conferenceFilter}${conferenceGroupFilter}${conferenceHotelFilter}${urlFilters}${paginationParams}${sortParams}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const data: OrderResponse = await response.json();
      setOrders(data.data || []);
      setTotalCount(data.meta?.pagination?.total || 0);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
      console.error('Orders error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Selection handlers
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allOrderIds = orders.map(order => order.documentId);
      setSelectedOrders(allOrderIds);
    } else {
      setSelectedOrders([]);
    }
  };

  const handleSelectOrder = (orderId: string) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  const handleBulkExport = () => {
    // Get selected orders from current orders array
    const selectedOrdersData = orders.filter(order => selectedOrders.includes(order.documentId));
    
    if (selectedOrdersData.length === 0) {
      return;
    }

    // Generate CSV content
    const headers = [
      'Confirmation',
      'Access Code',
      'Conference Group',
      'Contact Name',
      'Contact Email',
      'Hotel',
      'Order Rooms',
      'Order Status',
      'Invoiced',
      'Created Date',
      'Order Link'
    ];

    const csvRows = [headers.join(',')];

    selectedOrdersData.forEach(order => {
      // Format order rooms as requested: #xname with / separator
      let orderRoomsText = '';
      if (order.order_rooms && order.order_rooms.length > 0) {
        const hotelCounts: { [key: string]: number } = {};
        
        order.order_rooms.forEach(room => {
          const hotelName = room.conference_hotel_room?.name || 'Unknown Room';
          hotelCounts[hotelName] = (hotelCounts[hotelName] || 0) + 1;
        });
        
        orderRoomsText = Object.entries(hotelCounts)
          .map(([hotelName, count]) => `${count}x${hotelName}`)
          .join('/');
      }

      const row = [
        order.confirmation || 'Unconfirmed',
        order.accessCode || '-',
        order.conference_group?.longName || '-',
        `${order.contactLastName || ''}${order.contactLastName && order.contactFirstName ? ', ' : ''}${order.contactFirstName || ''}`,
        order.contactEmail || '-',
        order.conference_hotel?.hotel?.name || '-',
        orderRoomsText || '-',
        order.orderStatus || 'Unknown',
        order.invoiceSent ? 'Yes' : 'No',
        order.createdAt ? new Date(order.createdAt).toLocaleDateString() : '-',
        `https://citywidehousingsolutions.com/admin/order/${order.documentId}`
      ];
      
      // Escape commas and quotes in CSV values
      const escapedRow = row.map(field => {
        const stringField = String(field);
        if (stringField.includes(',') || stringField.includes('"') || stringField.includes('\n')) {
          return `"${stringField.replace(/"/g, '""')}"`;
        }
        return stringField;
      });
      
      csvRows.push(escapedRow.join(','));
    });

    // Create CSV blob and download
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `orders_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleSearch = () => {
    setPage(0); // Reset to first page when searching
    fetchOrders();
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(0);
    fetchOrders();
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0); // Reset to first page when sorting
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSplitOrder = (orderDocumentId: string) => {
    setSelectedOrderForSplit(orderDocumentId);
    setSplitOrderDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'submitted': return 'success';
      case 'draft': return 'default';
      case 'processing': return 'warning';
      case 'confirmed': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };
  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };
  if (loading && !orders.length) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="warning">
          Please log in to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 4, mt: 8 }}>
      <Container maxWidth="lg">

        <AdminTabs currentPath="/admin/orders" />
        
        <Paper sx={{ p: 3, mt: 3 }}>
          {/* URL Message Alert */}
          {searchParams.get('message') && (
            <Alert 
              color="warning" 
              sx={{ mb: 3 }}
            >
              {searchParams.get('message')}
              <Box mt={2}>
                <Button variant="outlined" size="small" sx={{ mt: 1 }} onClick={() => navigate('/admin/orders')}>
                  Dismiss & remove restriction
                </Button>
              </Box>
            </Alert>
          )}
          
          {/* Search Section */}
          <Box sx={{ mb: 3 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search orders by confirmation, email, name, or billing..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button 
                variant="contained" 
                onClick={handleSearch}
                disabled={loading}
              >
                Search
              </Button>
              <Button 
                variant="outlined" 
                onClick={handleClearSearch}
                disabled={loading}
              >
                Clear
              </Button>
            </Stack>
          </Box>

          {/* Filters Section */}
          <Box sx={{ mb: 3}}>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              {/* Conference Filter */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Conference</InputLabel>
                <Select
                  value={selectedConference}
                  label="Conference"
                  onChange={(e) => {
                    setSelectedConference(e.target.value);
                    setSelectedConferenceGroup('all'); // Reset conference group when conference changes
                    setSelectedConferenceHotel('all'); // Reset conference hotel when conference changes
                    setPage(0);
                  }}
                  disabled={loading}
                >
                  <MenuItem value="all">All Conferences</MenuItem>
                  {conferences.map((conference) => (
                    <MenuItem key={conference.documentId} value={conference.documentId}>
                      {conference.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {/* Conference Group Filter */}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Conference Group</InputLabel>
                <Select
                  value={selectedConferenceGroup}
                  label="Conference Group"
                  onChange={(e) => {
                    setSelectedConferenceGroup(e.target.value);
                    setPage(0);
                  }}
                  disabled={loading}
                >
                  <MenuItem value="all">All Groups</MenuItem>
                  {conferenceGroups
                    .filter(group => 
                      selectedConference === 'all' || 
                      group.conference?.documentId === selectedConference
                    )
                    .map((group) => (
                      <MenuItem key={group.documentId} value={group.documentId}>
                        {group.longName || group.name}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>

              {/* Conference Hotel Filter */}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Conference Hotel</InputLabel>
                <Select
                  value={selectedConferenceHotel}
                  label="Conference Hotel"
                  onChange={(e) => {
                    setSelectedConferenceHotel(e.target.value);
                    setPage(0);
                  }}
                  disabled={loading}
                >
                  <MenuItem value="all">All Hotels</MenuItem>
                  {conferenceHotels
                    .filter(hotelEntry => 
                      selectedConference === 'all' || 
                      hotelEntry.conference?.documentId === selectedConference
                    )
                    .map((hotelEntry) => (
                      <MenuItem key={hotelEntry.documentId} value={hotelEntry.documentId}>
                        {hotelEntry.hotel?.longName || hotelEntry.hotel?.name || 'Unknown Hotel'}
                      </MenuItem>
                    ))
                  }
                </Select>
              </FormControl>
              
              {/* Order Status Filter */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Order Status</InputLabel>
                <Select
                  value={showReceivedDraftOrders}
                  label="Order Status"
                  onChange={(e) => {
                    setShowReceivedDraftOrders(e.target.value as 'all' | 'received' | 'abandoned');
                    setPage(0); // Reset to first page when changing filter
                  }}
                  disabled={loading}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="received">Received</MenuItem>
                  <MenuItem value="abandoned">Abandoned</MenuItem>
                </Select>
              </FormControl>
              
              {/* Invoice Status Filter */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Invoice Status</InputLabel>
                <Select
                  value={invoiceFilter}
                  label="Invoice Status"
                  onChange={(e) => {
                    setInvoiceFilter(e.target.value as 'all' | 'invoiced' | 'not-invoiced');
                    setPage(0); // Reset to first page when changing filter
                  }}
                  disabled={loading}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="invoiced">Invoiced</MenuItem>
                  <MenuItem value="not-invoiced">Not Invoiced</MenuItem>
                </Select>
              </FormControl>
              
              {/* Credit Card Status Filter */}
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Credit Card</InputLabel>
                <Select
                  value={creditCardFilter}
                  label="Credit Card"
                  onChange={(e) => {
                    setCreditCardFilter(e.target.value as 'all' | 'added' | 'not-added');
                    setPage(0); // Reset to first page when changing filter
                  }}
                  disabled={loading}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="added">Added</MenuItem>
                  <MenuItem value="not-added">Not Added</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Error Display */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Orders Table */}
          {!loading && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {totalCount} Order{totalCount !== 1 ? 's' : ''} Found
              </Typography>
              
              {orders.length === 0 ? (
                <Alert severity="info">
                  No orders found. Try adjusting your search criteria.
                </Alert>
              ) : (
                <>
                  {/* Bulk Actions */}
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" color="primary.contrastText">
                        {selectedOrders.length} order{selectedOrders.length !== 1 ? 's' : ''} selected
                      </Typography>
                      <Button 
                        variant="contained" 
                        color="secondary"
                        onClick={handleBulkExport}
                        size="small"
                        disabled={selectedOrders.length === 0}
                      >
                        Export
                      </Button>
                      <Button 
                        variant="outlined" 
                        color="inherit"
                        onClick={() => setSelectedOrders([])}
                        size="small"
                        disabled={selectedOrders.length === 0}

                      >
                        Clear Selection
                      </Button>
                    </Stack>
                  </Box>
                  
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={selectedOrders.length > 0 && selectedOrders.length < orders.length}
                              checked={orders.length > 0 && selectedOrders.length === orders.length}
                              onChange={handleSelectAll}
                            />
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'confirmation'}
                              direction={orderBy === 'confirmation' ? order : 'asc'}
                              onClick={() => handleRequestSort('confirmation')}
                            >
                              Confirmation
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'conference_group'}
                              direction={orderBy === 'conference_group' ? order : 'asc'}
                              onClick={() => handleRequestSort('conference_group')}
                            >
                              Group
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'contactLastName'}
                              direction={orderBy === 'contactLastName' ? order : 'asc'}
                              onClick={() => handleRequestSort('contactLastName')}
                            >
                              Contact
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{maxWidth: 100}}>
                            <TableSortLabel
                              active={orderBy === 'conference_hotel'}
                              direction={orderBy === 'conference_hotel' ? order : 'asc'}
                              onClick={() => handleRequestSort('conference_hotel')}
                            >
                              Hotel
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{maxWidth: 100}}>
                            <TableSortLabel
                              active={orderBy === 'orderStatus'}
                              direction={orderBy === 'orderStatus' ? order : 'asc'}
                              onClick={() => handleRequestSort('orderStatus')}
                            >
                              Order Status
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'invoiceSent'}
                              direction={orderBy === 'invoiceSent' ? order : 'asc'}
                              onClick={() => handleRequestSort('invoiceSent')}
                            >
                              Invoice
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'creditCardAdded'}
                              direction={orderBy === 'creditCardAdded' ? order : 'asc'}
                              onClick={() => handleRequestSort('creditCardAdded')}
                            >
                              CC
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                              Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {orders.map((order) => (
                          <TableRow 
                            key={order.documentId}
                            hover
                            selected={selectedOrders.includes(order.documentId)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedOrders.includes(order.documentId)}
                                onChange={() => handleSelectOrder(order.documentId)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </TableCell>
                            <TableCell onClick={() => navigate(`/admin/order/${order.documentId}`)}>
                              <Typography variant="body2">
                                {order.confirmation || 'Unconfirmed'}
                              </Typography>
                            </TableCell>
                            <TableCell onClick={() => navigate(`/admin/order/${order.documentId}`)}>
                              <Typography variant="body2">
                                {order.conference_group?.longName || '-'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                {order.accessCode || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell onClick={() => navigate(`/admin/order/${order.documentId}`)}>
                              <Box>
                                <Typography variant="body2">
                                  {order.contactLastName || ''}{order.contactLastName && order.contactFirstName ? ', ' : ''}{order.contactFirstName || ''}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  {order.contactEmail || '-'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell onClick={() => navigate(`/admin/order/${order.documentId}`)}>
                              <Box>
                                <Typography variant="body2">
                                  {order.conference_hotel?.hotel?.name || '-'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                  {order.conference_hotel?.tier || '-'} • {order.order_rooms?.length || 0} room{order.order_rooms?.length !== 1 ? 's' : ''}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell onClick={() => navigate(`/admin/order/${order.documentId}`)}>
                              <Chip 
                                label={order.orderStatus || 'Unknown'} 
                                color={getStatusColor(order.orderStatus) as any}
                                size="small"
                              />
                            </TableCell>
                            <TableCell onClick={() => navigate(`/admin/order/${order.documentId}`)}>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <BuildIcon sx={{ 
                                  color: order.invoicePath ? 'success.main' : 'text.disabled', 
                                  fontSize: '1.2rem' 
                                }} />
                                {order.invoiceSent ? (
                                  <EmailIcon sx={{ color: 'success.main', fontSize: '1.2rem' }} />
                                ) : (
                                  <EmailOutlinedIcon sx={{ color: 'text.disabled', fontSize: '1.2rem' }} />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell onClick={() => navigate(`/admin/order/${order.documentId}`)}>
                              {order.creditCardAdded ? (
                                <CheckIcon sx={{ color: 'success.main' }} />
                              ) : (
                                <CloseIcon sx={{ color: 'error.main' }} />
                              )}
                            </TableCell>
                            <TableCell>
                              <Link href={`/admin/order/${order.documentId}`} underline="hover" color="primary" sx={{ mr: 1 }}>
                                View
                              </Link>
                            </TableCell>

                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 25, 50, 100, 1000]}
                  />
                </>
              )}
            </Box>
          )}
        </Paper>
      </Container>
      
      {/* Split Order Dialog */}
      <SplitOrder
        open={splitOrderDialogOpen}
        onClose={() => {setSplitOrderDialogOpen(false); fetchOrders();}}
        orderDocumentId={selectedOrderForSplit}
        onUpdate={() => {
          setSplitOrderDialogOpen(false);
          fetchOrders();
        }}
      />
    </Box>
  );
}